/**
 * Adding and subtracting 9, 8 and 7 (Meliesa, Oct 29): "I go through the entire series adding 9 to 1-digit numbers,
 * then take it to 2-digit numbers. Same with subtractions. Repeat for 8's and 7's."
 *   9 and 8 are two-square puzzles on the Map: add = back the bond, down a row; subtract = forward the bond, up a row.
 *   7 is on the keypad, the same direction as multiplication: "7 + 7 ends with a 4. 4 + 7 ends with a 1. 1 + 7 ends
 *   with an 8, and stays the same in the tens place — when you go up." Use the keypad for what the next answer ends
 *   with; then make sure the tens place is right.
 * Each practice step leads with the whole 1-digit series in order (`series*`), then 2-digit numbers.
 */
import { int } from "../core";
import type { Item, Skill } from "../types";
import { keypadRing, mapPuzzleUp } from "../visuals";
import { addMapItem } from "./level3-l2";

const hit = (raw: string, n: number) => raw.trim().replace(/,/g, "") === String(n);

/* ---------- Subtracting 9 or 8 on the Map: forward the bond, up a row ---------- */
export function subMapItem(sub: 9 | 8, n: number): Item {
  const bond = 10 - sub, ans = n - sub;
  return {
    id: `sub-n-map-puzzle:${n}-${sub}`,
    skill: `sub-n-map-puzzle/${sub}`,
    prompt: `${n} − ${sub} =`,
    visual: mapPuzzleUp(n, bond),
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      if (hit(raw, n - 10)) return { code: "submap-no-forward", hint: `You went up a row. Go forward ${bond} first.` };
      if (hit(raw, n - 10 - bond)) return { code: "submap-back", hint: `Forward ${bond}, not back. Then up a row.` };
      if (hit(raw, n + bond)) return { code: "submap-no-up", hint: `That's forward ${bond}. Now go up a row.` };
      return null;
    },
    hints: [`Start at ${n}. Go forward ${bond}. Where are you?`, "Now go up one row, same column.", `${n}: forward ${bond} is ${n + bond}, up a row is ${ans}.`],
  };
}

/** Starts where "forward the bond" stays on the same line (ones digit ≤ 9 − bond). */
const subStart = (rng: () => number, sub: 9 | 8): number => {
  const bond = 10 - sub;
  let n = int(rng, 20, 98);
  while (n % 10 > 9 - bond) n = int(rng, 20, 98);
  return n;
};

/* ---------- Adding and subtracting 7 on the keypad ---------- */
const CODE7 = [7, 4, 1, 8, 5, 2, 9, 6, 3, 0];
const ROW: Record<number, number> = { 7: 0, 8: 0, 9: 0, 4: 1, 5: 1, 6: 1, 1: 2, 2: 2, 3: 2, 0: 3 };
/** The next key in the sevens code, and whether the move goes UP the keypad. */
export function next7(d: number): { e: number; up: boolean } {
  const e = CODE7[(CODE7.indexOf(d) + 1) % 10]!;
  return { e, up: ROW[e]! < ROW[d]! };
}
export function prev7(d: number): { e: number; down: boolean } {
  const e = CODE7[(CODE7.indexOf(d) + 9) % 10]!;
  return { e, down: ROW[e]! > ROW[d]! };
}

export function add7Item(n: number): Item {
  const ans = n + 7, d = n % 10, { e, up } = next7(d);
  return {
    id: `add-7-keypad:${n}+7`,
    skill: "add-7-keypad",
    prompt: `${n} + 7 =`,
    visual: keypadRing(d),
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      const v = Number(raw.trim());
      if (Number.isFinite(v) && v % 10 === e && v !== ans) return { code: "keypad7-tens", hint: `It ends with ${e} — right. Now the tens: ${up ? "you went UP the keypad, so the tens stay the same." : "you didn't go up, so it's one more ten."}` };
      if (Number.isFinite(v) && Math.floor(v / 10) === Math.floor(ans / 10) && v % 10 !== e) return { code: "keypad7-ones", hint: `Find ${d} on the keypad and take one step in the sevens code. What key is next?` };
      return null;
    },
    hints: [`Find ${d} on the keypad. Take one step in the sevens code — the same way you count by 7s.`, `The next key is ${e}: the answer ends with ${e}. ${up ? "You went UP the keypad, so the tens stay the same." : "You didn't go up, so it's one more ten."}`, `${d} → ${e}${up ? ", going up: the tens stay" : ": one more ten"}. ${n} + 7 = ${ans}.`],
  };
}

export function sub7Item(n: number): Item {
  const ans = n - 7, d = n % 10, { e, down } = prev7(d);
  return {
    id: `sub-7-keypad:${n}-7`,
    skill: "sub-7-keypad",
    prompt: `${n} − 7 =`,
    visual: keypadRing(d),
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      const v = Number(raw.trim());
      if (Number.isFinite(v) && v % 10 === e && v !== ans) return { code: "keypad7-tens", hint: `It ends with ${e} — right. Now the tens: ${down ? "you went DOWN the keypad, so the tens stay the same." : "you didn't go down, so it's one less ten."}` };
      if (Number.isFinite(v) && Math.floor(v / 10) === Math.floor(ans / 10) && v % 10 !== e) return { code: "keypad7-ones", hint: `Find ${d} on the keypad and take one step BACK in the sevens code. What key comes before it?` };
      return null;
    },
    hints: [`Find ${d} on the keypad. Take one step BACK in the sevens code — the way you count backward by 7s.`, `The key before ${d} is ${e}: the answer ends with ${e}. ${down ? "You went DOWN the keypad, so the tens stay the same." : "You didn't go down, so it's one less ten."}`, `${d} → ${e}${down ? ", going down: the tens stay" : ": one less ten"}. ${n} − 7 = ${ans}.`],
  };
}

/* ---------- The series (each practice step starts with the whole 1-digit series, in order) ---------- */
export const seriesAdd9 = (): Item[] => [...Array.from({ length: 9 }, (_, i) => addMapItem(9, i + 1))];
export const seriesSub9 = (): Item[] => Array.from({ length: 9 }, (_, i) => subMapItem(9, 10 + i)); // 10 − 9 … 18 − 9
export const seriesAdd8 = (): Item[] => Array.from({ length: 8 }, (_, i) => addMapItem(8, i + 2)); // 2 + 8 … 9 + 8 (the book's starts)
export const seriesSub8 = (): Item[] => Array.from({ length: 8 }, (_, i) => subMapItem(8, 10 + i)); // 10 − 8 … 17 − 8
export const seriesAdd7 = (): Item[] => Array.from({ length: 10 }, (_, i) => add7Item(i)); // 0 + 7 … 9 + 7
export const seriesSub7 = (): Item[] => Array.from({ length: 10 }, (_, i) => sub7Item(7 + i)); // 7 − 7 … 16 − 7

export const sub9: Skill = { id: "sub-n-map-puzzle/9", name: "Subtract 9: forward 1 and up", gen: (rng) => subMapItem(9, subStart(rng, 9)) };
export const sub8Map: Skill = { id: "sub-n-map-puzzle/8", name: "Subtract 8: forward 2 and up", gen: (rng) => subMapItem(8, subStart(rng, 8)) };
export const add7: Skill = { id: "add-7-keypad", name: "Add 7 on the keypad", gen: (rng) => add7Item(int(rng, 10, 92)) };
export const sub7: Skill = { id: "sub-7-keypad", name: "Subtract 7 on the keypad", gen: (rng) => sub7Item(int(rng, 17, 99)) };

export const ADDSUB987_SKILLS: Skill[] = [sub9, sub8Map, add7, sub7];
